import type { Action } from './actions';

/**
 * A permission is an immutable machine identifier: `<module>.<resource>.<action>`.
 *
 * Display names and descriptions are translation keys, never literals — the
 * catalog is rendered in ten locales. Tenants compose custom roles from this
 * catalog but may not invent new permissions (decision D6).
 */
export interface PermissionDefinition {
  readonly key: string;
  readonly module: string;
  readonly resource: string;
  readonly action: Action;
  /** i18n key for the administrator-facing label. */
  readonly labelKey: string;
  /**
   * Sensitive permissions require an elevated confirmation step when granted,
   * and every use is audited. Kids, pastoral, finance and security land here.
   */
  readonly isSensitive: boolean;
  /**
   * The entitlement feature this permission depends on. A tenant may hold the
   * permission and still be denied because their plan does not include the
   * feature — installation and entitlement are separate concerns.
   */
  readonly featureKey: string | null;
}

function define(
  module: string,
  resource: string,
  actions: readonly Action[],
  options: { sensitive?: boolean; featureKey?: string | null } = {},
): PermissionDefinition[] {
  return actions.map((action) => ({
    key: `${module}.${resource}.${action}`,
    module,
    resource,
    action,
    labelKey: `permissions.${module}.${resource}.${action}`,
    isSensitive: options.sensitive ?? false,
    featureKey: options.featureKey ?? null,
  }));
}

/**
 * The Phase 0 + Phase 1 permission catalog.
 *
 * Later phases append to this list; they never renumber or rename an existing
 * key, because keys are persisted in role_permissions rows.
 */
export const PERMISSION_CATALOG: readonly PermissionDefinition[] = [
  // --- Platform administration (TABITA operators, not church roles) ----------
  ...define('platform', 'tenant', ['read', 'create', 'update', 'admin'], { sensitive: true }),
  ...define('platform', 'impersonation', ['create'], { sensitive: true }),
  ...define('platform', 'module', ['read', 'update', 'admin'], { sensitive: true }),
  ...define('platform', 'settings', ['read', 'update'], { sensitive: true }),
  // Shared, platform-wide catalogs under Mantenimientos — every church reads
  // them to fill out its own profile, only PLATFORM_SUPER_ADMIN maintains them.
  ...define('platform', 'denomination', ['read', 'create', 'update', 'archive', 'restore']),
  ...define('platform', 'council', ['read', 'create', 'update', 'archive', 'restore']),

  // --- Identity -------------------------------------------------------------
  ...define('identity', 'user', ['read', 'create', 'update', 'archive'], { sensitive: true }),
  ...define('identity', 'session', ['read', 'delete'], { sensitive: true }),
  ...define('identity', 'mfa', ['read', 'manage'], { sensitive: true }),

  // --- Tenancy --------------------------------------------------------------
  ...define('tenancy', 'tenant', ['read', 'update', 'admin']),
  ...define('tenancy', 'organization', ['read', 'create', 'update', 'archive']),
  ...define('tenancy', 'campus', ['read', 'create', 'update', 'archive']),
  ...define('tenancy', 'unit', ['read', 'create', 'update', 'archive']),
  ...define('tenancy', 'membership', ['read', 'create', 'update', 'archive'], { sensitive: true }),
  ...define('tenancy', 'setting', ['read', 'update']),
  ...define('tenancy', 'pastor', ['read', 'create', 'update', 'archive', 'restore']),

  // --- Authorization --------------------------------------------------------
  ...define('authorization', 'role', ['read', 'create', 'update', 'archive', 'assign'], {
    sensitive: true,
  }),
  ...define('authorization', 'permission', ['read']),

  // --- Entitlements and billing --------------------------------------------
  ...define('entitlement', 'plan', ['read']),
  ...define('entitlement', 'subscription', ['read', 'update'], { sensitive: true }),
  ...define('entitlement', 'usage', ['read']),
  ...define('billing', 'subscription', ['read', 'update'], { sensitive: true }),

  // --- Module registry ------------------------------------------------------
  ...define('module', 'installation', ['read', 'activate', 'update'], { sensitive: true }),

  // --- Audit ----------------------------------------------------------------
  ...define('audit', 'event', ['read', 'export'], { sensitive: true }),
  ...define('audit', 'security_event', ['read', 'export'], { sensitive: true }),

  // --- Files ("Mi Nube") ------------------------------------------------------
  // `archive` doubles as "move to trash"; `restore` takes it back out;
  // `delete` is permanent and only ever valid on a file already trashed —
  // real removal, not the archive-forever posture ADR-0006 gives church
  // records, because a personal drive file has no attendance/giving history
  // hanging off it the way a Person or a Group does.
  ...define('file', 'object', ['read', 'create', 'update', 'archive', 'restore', 'delete']),
  /** Browse anyone's Mi Nube, not just your own — administrative oversight,
   *  the same posture `people.person.delete` takes for a rare, sensitive
   *  override rather than everyday use. */
  ...define('file', 'object', ['admin'], { sensitive: true }),
  ...define('file', 'folder', ['read', 'create', 'update', 'delete']),
  // `manage` covers creating and revoking a share link — one decision made
  // by one person, the same reasoning `groups.member.manage` gives.
  ...define('file', 'share', ['read', 'manage']),

  // --- Search ---------------------------------------------------------------
  ...define('search', 'global', ['read']),

  // --- People (Phase 1) -----------------------------------------------------
  ...define('people', 'person', ['read', 'create', 'update', 'archive', 'restore'], {
    featureKey: 'people.directory',
  }),
  ...define('people', 'person', ['export', 'import'], {
    sensitive: true,
    featureKey: 'people.bulk_operations',
  }),
  /** Real deletion — separate from `archive`, sensitive, and only ever a
   *  true removal when the person has no history anywhere else in the
   *  platform; see `Person.delete()`. */
  ...define('people', 'person', ['delete'], {
    sensitive: true,
    featureKey: 'people.directory',
  }),
  ...define('people', 'household', ['read', 'create', 'update', 'archive'], {
    featureKey: 'people.households',
  }),
  ...define('people', 'relationship', ['read', 'create', 'update', 'delete'], {
    featureKey: 'people.relationships',
  }),
  ...define('people', 'membership', ['read', 'create', 'update', 'archive'], {
    featureKey: 'people.membership',
  }),
  ...define('people', 'note', ['read', 'create', 'update', 'archive']),
  ...define('people', 'tag', ['read', 'create', 'update', 'archive']),
  ...define('people', 'directory', ['read'], { featureKey: 'people.directory' }),
  ...define('people', 'merge', ['manage'], { sensitive: true }),
  ...define('people', 'customField', ['read', 'create', 'update', 'archive', 'restore'], {
    featureKey: 'people.custom_fields',
  }),
  ...define('people', 'transfer', ['read', 'create'], { featureKey: 'people.directory' }),
  // `manage` covers approving, rejecting and cancelling — one decision made
  // by one person, the same reasoning `groups.member.manage` gives.
  ...define('people', 'transfer', ['manage'], {
    sensitive: true,
    featureKey: 'people.directory',
  }),
  // Pastoral contact history is sensitive by nature — the same posture
  // Kids and Giving take, not because the module itself is risky.
  ...define('people', 'followup', ['read', 'create'], {
    sensitive: true,
    featureKey: 'people.directory',
  }),
  ...define('people', 'growthStep', ['read', 'create', 'update', 'archive', 'restore'], {
    featureKey: 'people.directory',
  }),
  ...define('people', 'growthProgress', ['read', 'manage'], { featureKey: 'people.directory' }),

  // --- Groups (Phase 1) -----------------------------------------------------
  ...define('groups', 'group', ['read', 'create', 'update', 'archive', 'restore'], {
    featureKey: 'groups.groups',
  }),
  // `manage` rather than create/update/delete: adding someone, changing their
  // role and ending their membership are one job done by one person, and three
  // grants nobody would assign separately are three chances to assign wrongly.
  ...define('groups', 'member', ['read', 'manage'], { featureKey: 'groups.groups' }),
  /** Promoting someone to LEADER/CO_LEADER, separate from ordinary roster
   *  management — a role that carries real authority over the group, split
   *  out the same way `people.person.delete` was split from `.archive`. */
  ...define('groups', 'member', ['assign'], { sensitive: true, featureKey: 'groups.groups' }),

  // --- Events (Phase 1) -------------------------------------------------------
  ...define(
    'events',
    'event',
    ['read', 'create', 'update', 'publish', 'cancel', 'archive', 'restore'],
    {
      featureKey: 'events.registration',
    },
  ),
  // `manage` rather than create/update/delete, the same reasoning Groups
  // applies to a roster: registering someone and cancelling them is one job.
  ...define('events', 'registration', ['read', 'manage'], { featureKey: 'events.registration' }),

  // --- Forms (Phase 1) ---------------------------------------------------------
  ...define('forms', 'form', ['read', 'create', 'update', 'publish', 'archive', 'restore'], {
    featureKey: 'forms.forms',
  }),
  ...define('forms', 'submission', ['read', 'create'], { featureKey: 'forms.forms' }),

  // --- Attendance (Phase 1) -----------------------------------------------------
  // `update` covers checking a record back out — there is no delete and no
  // archive, the same reasoning as everywhere else that a correction is a
  // state change, not an erasure.
  ...define('attendance', 'record', ['read', 'create', 'update'], {
    featureKey: 'attendance.check_in',
  }),

  // --- Academic -----------------------------------------------------------------
  ...define('academic', 'school', ['read', 'create', 'update', 'archive', 'restore'], {
    featureKey: 'academic.courses',
  }),
  ...define('academic', 'level', ['read', 'create', 'update', 'archive', 'restore'], {
    featureKey: 'academic.courses',
  }),
  ...define('academic', 'subject', ['read', 'create', 'update', 'archive', 'restore'], {
    featureKey: 'academic.courses',
  }),
  ...define('academic', 'teacher', ['read', 'create', 'update', 'archive', 'restore'], {
    featureKey: 'academic.courses',
  }),
  ...define('academic', 'courseSection', ['read', 'create', 'update', 'archive', 'restore'], {
    featureKey: 'academic.courses',
  }),
  // `manage` rather than create/update/delete, the same reasoning Groups
  // applies to a roster: enrolling someone and ending their enrollment is
  // one job done by one person. `self` is the auto-matrícula grant — a
  // member enrolling (or viewing the transcript of) only themselves, wide
  // open the same way `people.directory.read` is, distinct from `manage`
  // which acts on anyone.
  ...define('academic', 'enrollment', ['read', 'manage', 'self'], {
    featureKey: 'academic.courses',
  }),
  // `create` is wide open the same way `enrollment.self` is — anyone can
  // ask for credit to be recognized. `manage` is the sensitive half —
  // approving or rejecting one — the same reasoning `people.transfer.manage`
  // gives for a single grant covering both outcomes of one decision.
  ...define('academic', 'homologation', ['read', 'create'], { featureKey: 'academic.courses' }),
  ...define('academic', 'homologation', ['manage'], {
    sensitive: true,
    featureKey: 'academic.courses',
  }),

  // --- Kids -------------------------------------------------------------------
  // `update` covers checking a child back out, the same reasoning Attendance
  // applies — a pickup is a state change, not a new record.
  ...define('kids', 'checkin', ['read', 'create', 'update'], {
    sensitive: true,
    featureKey: 'kids.checkin',
  }),

  // --- Giving -----------------------------------------------------------------
  ...define('giving', 'donation', ['read', 'create', 'update', 'refund'], {
    sensitive: true,
    featureKey: 'giving.donations',
  }),

  // --- Finance ----------------------------------------------------------------
  ...define('finance', 'account', ['read', 'create', 'update', 'archive'], {
    sensitive: true,
  }),
  ...define('finance', 'journal_entry', ['read', 'create'], { sensitive: true }),

  // --- Notifications ------------------------------------------------------------
  ...define('notification', 'log', ['read'], { sensitive: true }),

  // --- Communications ---------------------------------------------------------
  // `sensitive` because a campaign reaches many people at once — the same
  // audit-everywhere posture as kids, pastoral and finance actions, not
  // because the content itself is confidential.
  ...define('communications', 'campaign', ['read', 'create', 'update', 'archive', 'send'], {
    sensitive: true,
  }),

  // --- Calendar -----------------------------------------------------------------
  ...define('calendar', 'calendar', ['read', 'create', 'update', 'archive'], {
    featureKey: 'calendar.access',
  }),
  ...define('calendar', 'event', ['read', 'create', 'update', 'delete'], {
    featureKey: 'calendar.access',
  }),

  // --- Inventory ----------------------------------------------------------------
  ...define('inventory', 'category', ['read', 'create', 'update', 'archive', 'restore'], {
    featureKey: 'inventory.inventory',
  }),
  ...define('inventory', 'location', ['read', 'create', 'update', 'archive', 'restore'], {
    featureKey: 'inventory.inventory',
  }),
  ...define('inventory', 'supplier', ['read', 'create', 'update', 'archive', 'restore'], {
    featureKey: 'inventory.inventory',
  }),
  ...define('inventory', 'item', ['read', 'create', 'update', 'archive', 'restore'], {
    featureKey: 'inventory.inventory',
  }),
  // Read-only ledger plus append; a movement is never edited or deleted once
  // recorded — see `InventoryMovement` in the Prisma schema.
  ...define('inventory', 'movement', ['read', 'create'], { featureKey: 'inventory.inventory' }),
  // `manage` covers checking out, returning and reporting lost — one decision
  // made by one person, the same reasoning `groups.member.manage` gives.
  ...define('inventory', 'checkout', ['read', 'manage'], { featureKey: 'inventory.inventory' }),
  ...define('inventory', 'maintenance', ['read', 'create', 'update'], {
    featureKey: 'inventory.inventory',
  }),
];

/** Fast lookup by key. Built once; the catalog is static. */
const CATALOG_BY_KEY = new Map(PERMISSION_CATALOG.map((p) => [p.key, p]));

export function findPermission(key: string): PermissionDefinition | undefined {
  return CATALOG_BY_KEY.get(key);
}

export function permissionExists(key: string): boolean {
  return CATALOG_BY_KEY.has(key);
}

export function permissionsForModule(module: string): PermissionDefinition[] {
  return PERMISSION_CATALOG.filter((p) => p.module === module);
}

export function sensitivePermissions(): PermissionDefinition[] {
  return PERMISSION_CATALOG.filter((p) => p.isSensitive);
}

/** Every distinct permission key. Used to seed and reconcile the database catalog. */
export const ALL_PERMISSION_KEYS: readonly string[] = PERMISSION_CATALOG.map((p) => p.key);
